/**
 * Returns the default post-login landing path for a given user object.
 * Conservative: unknown roles fall through to /dashboard.
 */
export function getRoleDefaultPath(user) {
  switch (user?.role) {
    case 'broker':            return '/broker-dashboard';
    case 'carrier':           return '/carrier-portal';
    case 'cpa':               return '/cpa-dashboard';
    case 'lawyer':            return '/lawyer-dashboard';
    case 'firm_admin':        return '/firm-dashboard';
    case 'mga':               return '/mga-dashboard';
    case 'principal_broker':  return '/pb-dashboard';
    case 'deputy_pb':         return '/my-firms';
    default:                  return '/dashboard';
  }
}

export const ROUTES = Object.freeze({
  // ── Core ────────────────────────────────────────────────────────────────────
  LOGIN:                   '/login',
  DASHBOARD:               '/dashboard',
  PROFILE_SETUP:           '/profile-setup',

  // ── PB / Compliance ─────────────────────────────────────────────────────────
  RENEWALS:                '/renewals',
  PB_PROFILE:              '/pb-profile',
  PB_ONBOARDING:           '/pb-onboarding',
  CE_TRACKER:              '/ce-tracker',
  LICENSING:               '/licensing',
  MY_FIRMS:                '/my-firms',
  COMPLIANCE_CALENDAR:     '/compliance-calendar',
  COMPLIANCE_QUESTIONS:    '/compliance-questions',
  COMPLIANCE_ANSWERS:      '/compliance-answers',
  COMPLAINTS:              '/complaints',
  REGULATORY_CHANGES:      '/regulatory-changes',
  FILING_VAULT:            '/filing-vault',
  FORM1:                   '/form1',
  SUPERVISION:             '/supervision',
  AUDIT_RESPONSE:          '/audit-response',
  LIABILITY_SHIELD:        '/liability-shield',
  PROFESSIONAL_DIRECTORY:  '/professional-directory',

  // ── Tools ────────────────────────────────────────────────────────────────────
  COMMUNICATIONS:          '/communications',
  INBOUND_REVIEW:          '/inbound-review',
  CARRIER_PORTAL:          '/carrier-portal',
  REGULATORY_INTELLIGENCE: '/regulatory-intelligence',
  REGULATORY_MAP:          '/regulatory-map',
  DOCUMENT_REPOSITORY:     '/document-repository',
  BROKER_DOCUMENTS:        '/broker-documents',
  REGULATORY_FILINGS:      '/regulatory-filings',
  ONBOARDING_HUB:          '/onboarding-hub',

  // ── Account ─────────────────────────────────────────────────────────────────
  MFA_SETUP:               '/mfa-setup',
  PROFILE:                 '/profile',

  // ── Role dashboards ─────────────────────────────────────────────────────────
  CPA_DASHBOARD:           '/cpa-dashboard',
  LAWYER_DASHBOARD:        '/lawyer-dashboard',

  // ── Admin ────────────────────────────────────────────────────────────────────
  SUPER_ADMIN:             '/super-admin',
  MODE_SELECT:             '/mode-select',

  // ── CE Course Finder ─────────────────────────────────────────────────────────
  CE_COURSES:              '/ce-courses',

  // ── Regulator Access Centre ───────────────────────────────────────────────────
  REGULATOR_ACCESS:        '/regulator-access',

  // ── Multi-Licence CE Rules Engine ─────────────────────────────────────────────
  MULTI_LICENCE_CE:        '/multi-licence-ce',

  // ── Licence Wallet ────────────────────────────────────────────────────────────
  LICENCE_WALLET:          '/licence-wallet',
  LICENCE_CHART_IMPORT:    '/licence-chart-import',

  // ── Industry Events Finder ────────────────────────────────────────────────────
  INDUSTRY_EVENTS:         '/industry-events',

  // ── Broker Readiness Dashboard ────────────────────────────────────────────────
  BROKER_READINESS:        '/broker-readiness',

  // ── Broker Compliance Deadline Calendar ───────────────────────────────────────
  BROKER_DEADLINES:        '/broker-deadlines',

  // ── Compliance Tasks & Attestations ──────────────────────────────────────────
  ATTESTATIONS:            '/broker/attestations',
  BROKER_ATTESTATIONS:     '/attestations',
  ADMIN_ATTESTATIONS:      '/admin/attestations',

  // ── Notification History ──────────────────────────────────────────────────────
  NOTIFICATION_HISTORY:    '/notification-history',

  // ── Carrier ──────────────────────────────────────────────────────────────────
  CARRIER_INTAKE:          '/carrier-intake',
  CARRIER_AUDITS:          '/carrier-audits',

  // ── Firm document ingestion ───────────────────────────────────────────────
  FIRM_DOCUMENTS:          '/firms/:firmId/documents',

  // ── Broker Self-Service ───────────────────────────────────────────────────────
  BROKER_CE_UPLOAD:        '/broker/ce-upload',
  BROKER_LICENCE_UPLOAD:   '/broker/licence-upload',
  BROKER_DECLARATION:      '/broker/declaration',
  PB_REVIEW_QUEUE:         '/pb/review-queue',

  // ── E&O Tracking ─────────────────────────────────────────────────────────────
  EO_TRACKING:             '/eo-tracking',

  // ── Audit Binder ─────────────────────────────────────────────────────────────
  AUDIT_BINDER:            '/audit-binder',

  // ── Role Management ───────────────────────────────────────────────────────────
  ROLE_MANAGEMENT:         '/role-management',

  // ── Broker-specific paths ────────────────────────────────────────────────────
  BROKER_DASHBOARD:        '/broker-dashboard',
  BROKER_PROFILE:          '/broker-profile',
  CARRIER_APPOINTMENTS:    '/carrier-appointments',
  MY_SUPERVISION:          '/my-supervision',
  DEADLINES:               '/deadlines',
  BROKER_CRC_UPLOAD:       '/broker/crc-upload',

  // ── Broker Onboarding Wizard ──────────────────────────────────────────────────
  BROKER_ONBOARDING:       '/broker-onboarding',

  // ── Legal acceptance gate ─────────────────────────────────────────────────────
  ACCEPT_TERMS:            '/accept-terms',

  // ── MGA Dashboard ─────────────────────────────────────────────────────────────
  MGA_DASHBOARD:           '/mga-dashboard',

  // ── Firm Admin ───────────────────────────────────────────────────────────────
  FIRM_DASHBOARD:          '/firm-dashboard',
  FIRM_CONTACTS:           '/firm-contacts',
  FIRM_CONTACTS_MODULE:    '/firm-contacts/:firmId',
  FIRM_INVITE:             '/firm-invite/:token',
  FIRM_REGISTER:           '/register',

  // ── Network Dashboard ────────────────────────────────────────────────────────
  NETWORK_DASHBOARD:       '/network-dashboard',

  // ── RIBO E&O / Fidelity Bond Renewal Workflow ────────────────────────────────
  RIBO_EO:                 '/ribo-eo/:firmId',

  // ── Audit Centre ─────────────────────────────────────────────────────────────
  AUDIT_CENTRE:            '/audit-centre',

  // ── Jurisdiction Statements of Compliance ────────────────────────────────────
  JURISDICTION_STATEMENTS: '/jurisdiction-statements',

  // ── CAMGA Pipeline ───────────────────────────────────────────────────────────
  CAMGA_PIPELINE:          '/camga-pipeline',

  // ── PB Dashboard ─────────────────────────────────────────────────────────────
  PB_DASHBOARD:            '/pb-dashboard',

  // ── Conferences (MGA conference/trade-show calendar — distinct from the
  //    CE Course Finder's own "Industry Events" page at INDUSTRY_EVENTS above) ──
  CONFERENCES:             '/conferences',
  HOSPITALITY_REGISTER:    '/hospitality-register',

  // ── Aliases ──────────────────────────────────────────────────────────────────
  OVERVIEW:                '/overview',

  // ── Firm Setup Wizard (first-login, full-screen) ────────────────────────────
  FIRM_SETUP:              '/firm-setup',

  // ── Marketing / public ───────────────────────────────────────────────────────
  TRIAL_SIGNUP:            '/trial-signup',
});
